<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  acceptCheckSheet,
  approveCheckSheet,
  attachCheckSheetDrawing,
  attachCheckSheetPhoto,
  detachCheckSheetPhoto,
  fetchCheckSheetPdf,
  generateCheckSheetSummary,
  getCheckSheet,
  getCheckSheetHistory,
  getDownloadUrl,
  getFormTemplate,
  getMe,
  getProject,
  listDrawings,
  listWorkPacks,
  removeCheckSheetDrawing,
  reopenCheckSheet,
  sha256OfFile,
  signOffCheckSheet,
  uploadFile,
  type DrawingRow,
  type FormTemplateField,
} from "@/api/client";
import { loadPdf, renderPdfPage } from "@/lib/pdf";
import DiaryMarkedUpDrawing from "@/components/DiaryMarkedUpDrawing.vue";
import FilePhotoGrid from "@/components/FilePhotoGrid.vue";
import PageLayout from "@/components/PageLayout.vue";
import SaveStatus from "@/components/SaveStatus.vue";
import SiteHazardsNotice from "@/components/SiteHazardsNotice.vue";
import SignaturePad from "@/components/SignaturePad.vue";
import { usePermissions } from "@/lib/permissions";

const route = useRoute();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();

const { data, isPending, isError, error } = useQuery({
  queryKey: ["check-sheet", id],
  queryFn: () => getCheckSheet(id.value),
});

// --- marked-up drawings (visual evidence) — same source as the diary ---
const projectId = computed(() => data.value?.project_id ?? null);
const { data: packs } = useQuery({
  queryKey: ["work-packs", projectId],
  queryFn: () => listWorkPacks(projectId.value),
  enabled: () => !!projectId.value,
});
// Project site hazards — surfaced as a caution while inspecting.
const { data: project } = useQuery({
  queryKey: ["project", projectId],
  queryFn: () => getProject(projectId.value as string),
  enabled: () => !!projectId.value,
});
const attachedFileIds = computed(
  () => new Set((data.value?.drawings ?? []).map((d) => d.file_id as string)),
);
// Photo evidence captured against the sheet's photo fields (mobile, in field).
const photoFileIds = computed(() =>
  (data.value?.photos ?? [])
    .map((p) => p.file_id as string | undefined)
    .filter((x): x is string => !!x),
);
const availableDrawings = computed(() =>
  (packs.value ?? [])
    .flatMap((pack) =>
      pack.documents
        .filter((doc) => doc.markup && doc.file_id)
        .map((doc) => ({ pack, doc })),
    )
    .filter(({ doc }) => !attachedFileIds.value.has(doc.file_id as string)),
);
const showAttachDrawing = ref(false);
async function attachDrawing(
  pack: { id: string },
  doc: { document_id: string; title: string; file_id: string | null; markup?: Record<string, unknown> | null },
): Promise<void> {
  if (!doc.file_id) return;
  await attachCheckSheetDrawing(id.value, {
    title: doc.title,
    file_id: doc.file_id,
    markup: doc.markup ?? null,
    source_work_pack_id: pack.id,
    source_document_id: doc.document_id,
  });
  await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
  markSaved();
  showAttachDrawing.value = false;
}
async function removeDrawing(drawingId: string): Promise<void> {
  await removeCheckSheetDrawing(id.value, drawingId);
  await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
  markSaved();
}

// --- photos: attach on the web (parity with mobile field capture) ---
const photoBusy = ref(false);
const photoError = ref<string | null>(null);
async function onPhotoPick(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  if (!files.length) return;
  photoBusy.value = true;
  photoError.value = null;
  try {
    for (const file of files) {
      const sha256 = await sha256OfFile(file);
      const fileId = await uploadFile(file, "inspection_photo");
      await attachCheckSheetPhoto(id.value, {
        field_path: "evidence",
        file_id: fileId,
        sha256,
        mime_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
    markSaved();
  } catch (err) {
    photoError.value = err instanceof Error ? err.message : String(err);
  } finally {
    photoBusy.value = false;
    input.value = "";
  }
}
async function removePhoto(fileId: string): Promise<void> {
  const p = (data.value?.photos ?? []).find(
    (x) => (x as Record<string, unknown>).file_id === fileId,
  ) as Record<string, unknown> | undefined;
  await detachCheckSheetPhoto(id.value, {
    field_path: (p?.field_path as string) || "evidence",
    file_id: fileId,
  });
  await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
  markSaved();
}

// --- attach a page from the Drawings register (same as the diary) ---
const { data: registerDrawings } = useQuery({
  queryKey: ["drawings", projectId],
  queryFn: () => listDrawings(projectId.value),
  enabled: () => !!projectId.value,
});
const showRegisterPicker = ref(false);
const selectedRegisterDrawing = ref<DrawingRow | null>(null);
const drawBusy = ref(false);

interface RegisterPage {
  pageNumber: number;
  fileId: string;
  mimeType: string;
  filePage: number;
}
function registerPages(d: DrawingRow): RegisterPage[] {
  const rev =
    d.revisions.find((r) => r.revision_id === d.current_revision_id) ??
    d.revisions[d.revisions.length - 1];
  if (!rev) return [];
  const out: RegisterPage[] = [];
  let n = 0;
  for (const up of [...rev.uploads].sort((a, b) => a.position - b.position)) {
    for (let i = 1; i <= up.page_count; i += 1) {
      n += 1;
      out.push({
        pageNumber: n,
        fileId: up.file_id,
        mimeType: up.mime_type,
        filePage: i,
      });
    }
  }
  return out;
}
async function snapshotPageFile(page: RegisterPage): Promise<string> {
  if (page.mimeType !== "application/pdf") return page.fileId;
  const url = await getDownloadUrl(page.fileId);
  const doc = await loadPdf(url);
  try {
    const { canvas } = await renderPdfPage(doc, page.filePage, 1400);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) throw new Error("Could not render the PDF page.");
    const file = new File([blob], `drawing-p${page.pageNumber}.png`, {
      type: "image/png",
    });
    return uploadFile(file, "drawing_page");
  } finally {
    await doc.destroy().catch(() => {});
  }
}
async function attachFromRegister(
  d: DrawingRow,
  page: RegisterPage,
): Promise<void> {
  drawBusy.value = true;
  try {
    const fileId = await snapshotPageFile(page);
    const shapes = (d.annotations?.[String(page.pageNumber)] as unknown[]) ?? [];
    await attachCheckSheetDrawing(id.value, {
      title: `${d.name} · p${page.pageNumber}`,
      file_id: fileId,
      markup: { shapes },
      source_drawing_id: d.id,
      page_number: page.pageNumber,
    });
    await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
    markSaved();
    showRegisterPicker.value = false;
    selectedRegisterDrawing.value = null;
  } finally {
    drawBusy.value = false;
  }
}

const { data: history } = useQuery({
  queryKey: ["check-sheet-history", id],
  queryFn: () => getCheckSheetHistory(id.value),
});

function fmtDateTime(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "—";
}
function attribution(
  name: string | null | undefined,
  at: string | null | undefined,
): string | null {
  if (!name && !at) return null;
  return [name || "—", at ? fmtDateTime(at) : null].filter(Boolean).join(" · ");
}

// Sign-off geo-tag → coords + a maps link (brief §1.4).
const signoffGeo = computed(() => {
  const g = data.value?.signed_off_gps;
  if (!g || typeof g.lat !== "number" || typeof g.lng !== "number") return null;
  const acc = g.accuracy_m != null ? ` ±${Math.round(g.accuracy_m)}m` : "";
  return {
    label: `${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}${acc}`,
    href: `https://www.google.com/maps?q=${g.lat},${g.lng}`,
  };
});

const templateId = computed(() => data.value?.template_id);
const { data: template } = useQuery({
  queryKey: ["form-template", templateId],
  queryFn: () => getFormTemplate(templateId.value as string),
  enabled: computed(() => !!templateId.value),
});

interface Row {
  key: string;
  def: FormTemplateField;
  value: unknown;
}
interface Group {
  title: string;
  rows: Row[];
}

// Group the answers by the template's ui.groups (falling back to a single
// group of every property if the template has no grouping).
const groups = computed<Group[]>(() => {
  const t = template.value;
  const answers = data.value?.answers ?? {};
  if (!t) return [];
  const props = t.schema.properties;
  const uiGroups =
    t.ui?.groups ?? [{ title: "", fields: Object.keys(props) }];
  return uiGroups.map((g) => ({
    title: g.title ?? "",
    rows: g.fields
      .filter((k) => props[k])
      .map((k) => ({ key: k, def: props[k], value: answers[k] })),
  }));
});

const YESNO: Record<string, string> = { yes: "Yes", no: "No", na: "N/A" };
function asMeasured(v: unknown): { actual?: unknown; compliant?: string } {
  return v && typeof v === "object" ? (v as Record<string, never>) : {};
}
function plain(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}
function gridRows(v: unknown): Array<Record<string, unknown>> {
  return Array.isArray(v) ? (v as Array<Record<string, unknown>>) : [];
}
function gridCell(col: { key: string; type: string }, row: Record<string, unknown>): string {
  const raw = row[col.key];
  if (col.type === "yes-no-na") return YESNO[raw as string] ?? "—";
  return plain(raw);
}

const busy = ref(false);
const actionError = ref<string | null>(null);

// Timestamp of the last successful save on this sheet — shown in the header.
const savedAt = ref<number | null>(null);
function markSaved(): void {
  savedAt.value = Date.now();
}

async function act(fn: () => Promise<void>): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await fn();
    await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
    await queryClient.invalidateQueries({ queryKey: ["check-sheets"] });
    markSaved();
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

const pdfBusy = ref(false);
async function downloadPdf(): Promise<void> {
  pdfBusy.value = true;
  actionError.value = null;
  try {
    const blob = await fetchCheckSheetPdf(id.value);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    pdfBusy.value = false;
  }
}

// Inspector sign-off on the web — the office/desk equivalent of the in-field
// mobile sign-off, for inspections completed at a desk.
const showSignoff = ref(false);
const signoffSignature = ref<string | null>(null);
async function signOff(): Promise<void> {
  if (!signoffSignature.value) return;
  await act(() => signOffCheckSheet(id.value, signoffSignature.value as string));
  showSignoff.value = false;
  signoffSignature.value = null;
}

// Approver digitally signs off before locking the record (brief §1.5).
const showApprove = ref(false);
const approveSignature = ref<string | null>(null);
async function approve(): Promise<void> {
  if (!approveSignature.value) return;
  await act(() => approveCheckSheet(id.value, approveSignature.value as string));
  showApprove.value = false;
  approveSignature.value = null;
}

// External clients get controlled, read-only access and may accept (§1.5).
const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
const isClient = computed(() => (me.value?.roles ?? []).includes("client"));
// Approver-role gate (matches the server + mobile) for the sign-off actions.
const { canApprove } = usePermissions();
const showAccept = ref(false);
const acceptSignature = ref<string | null>(null);
async function acceptRecord(): Promise<void> {
  if (!acceptSignature.value) return;
  await act(() => acceptCheckSheet(id.value, acceptSignature.value as string));
  showAccept.value = false;
  acceptSignature.value = null;
}
function sendBack(): void {
  const reason = window.prompt("Reason for sending this check sheet back?");
  if (reason && reason.trim()) void act(() => reopenCheckSheet(id.value, reason.trim()));
}

const summaryBusy = ref(false);
async function generateSummary(): Promise<void> {
  summaryBusy.value = true;
  actionError.value = null;
  try {
    await generateCheckSheetSummary(id.value);
    await queryClient.invalidateQueries({ queryKey: ["check-sheet", id] });
    await queryClient.invalidateQueries({ queryKey: ["check-sheet-history", id] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    summaryBusy.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'check-sheets', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Check sheets
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            {{ template?.name ?? data.template_id }}
          </h1>
          <p class="mt-1 text-sm text-neutral-500">
            {{ data.inspection_type.replace("_", " ") }} ·
            {{ data.defect_count }} defect(s) · {{ data.photo_count }} photo(s)
            <span v-if="data.signed_off_at">
              · signed off {{ new Date(data.signed_off_at).toLocaleString() }}</span>
            <span v-if="data.approved_at">
              · approved {{ new Date(data.approved_at).toLocaleString() }}</span>
          </p>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <div class="flex items-center gap-2">
            <SaveStatus
              :saving="busy"
              :saved-at="savedAt"
            />
            <span
              class="rounded-full px-3 py-1 text-xs font-medium"
              :class="
                data.status === 'approved'
                  ? 'bg-semantic-success/10 text-semantic-success'
                  : data.status === 'signed_off'
                    ? 'bg-semantic-warning/10 text-semantic-warning'
                    : data.status === 'reopened'
                      ? 'bg-semantic-danger/10 text-semantic-danger'
                      : 'bg-neutral-100 text-neutral-500'
              "
            >
              {{ data.status.replace("_", " ") }}
            </span>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="pdfBusy"
              @click="downloadPdf"
            >
              {{ pdfBusy ? "…" : "PDF" }}
            </button>
            <button
              v-if="!isClient && data.status !== 'signed_off' && data.status !== 'approved'"
              type="button"
              class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy"
              @click="showSignoff = !showSignoff"
            >
              {{ busy ? "…" : "Sign off" }}
            </button>
            <button
              v-if="data.status === 'signed_off' && canApprove"
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="busy"
              @click="sendBack"
            >
              Send back
            </button>
            <button
              v-if="data.status === 'signed_off' && canApprove"
              type="button"
              class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy"
              @click="showApprove = !showApprove"
            >
              {{ busy ? "…" : "Approve (CRE)" }}
            </button>
            <button
              v-if="data.status === 'approved' && isClient && !data.client_accepted_at"
              type="button"
              class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy"
              @click="showAccept = !showAccept"
            >
              Accept record
            </button>
            <span
              v-else-if="isClient && data.client_accepted_at"
              class="self-center text-sm text-semantic-success"
            >✓ Accepted</span>
          </div>
        </div>
      </header>

      <!-- Site hazards on this project — a caution while inspecting -->
      <SiteHazardsNotice
        v-if="(project?.hazards ?? []).length"
        :hazards="project?.hazards ?? []"
        readonly
      />

      <!-- Inspector sign-off (web / desk) -->
      <div
        v-if="showSignoff && !isClient && data.status !== 'signed_off' && data.status !== 'approved'"
        class="grid gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-4"
      >
        <p class="text-sm text-neutral-600">
          Sign below to sign off this check sheet as the inspector. It then goes
          to the CRE for approval.
        </p>
        <SignaturePad v-model="signoffSignature" />
        <div class="flex gap-2">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="busy || !signoffSignature"
            @click="signOff"
          >
            Sign off
          </button>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            @click="showSignoff = false"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Approver signs off before locking the record (§1.5) -->
      <div
        v-if="showApprove && data.status === 'signed_off' && canApprove"
        class="grid gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-4"
      >
        <p class="text-sm text-neutral-600">
          Sign below to approve and lock this check sheet.
        </p>
        <SignaturePad v-model="approveSignature" />
        <div class="flex gap-2">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="busy || !approveSignature"
            @click="approve"
          >
            Approve &amp; lock
          </button>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            @click="showApprove = false"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Client accepts the approved record (§1.5) -->
      <div
        v-if="showAccept && isClient && data.status === 'approved'"
        class="grid gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-4"
      >
        <p class="text-sm text-neutral-600">
          Sign below to accept this approved check sheet.
        </p>
        <SignaturePad v-model="acceptSignature" />
        <div class="flex gap-2">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="busy || !acceptSignature"
            @click="acceptRecord"
          >
            Accept
          </button>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            @click="showAccept = false"
          >
            Cancel
          </button>
        </div>
      </div>

      <p
        v-if="actionError"
        class="rounded-md bg-semantic-danger/10 p-3 text-sm text-semantic-danger"
      >
        {{ actionError }}
      </p>

      <!-- AI summary for the approving CRE -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Summary
          </h2>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="summaryBusy"
            @click="generateSummary"
          >
            {{ summaryBusy ? "Generating…" : data.ai_summary ? "Regenerate" : "Generate summary" }}
          </button>
        </div>
        <p
          v-if="data.ai_summary"
          class="mt-3 whitespace-pre-line text-sm text-neutral-800"
        >
          {{ data.ai_summary }}
        </p>
        <p
          v-else
          class="mt-3 text-sm text-neutral-400"
        >
          No summary yet — generate a plain-English overview of this ITP for the CRE.
        </p>
        <p
          v-if="data.ai_summary_at"
          class="mt-2 text-xs text-neutral-400"
        >
          {{ data.ai_summary_model ? `AI-generated (${data.ai_summary_model})` : "Auto-generated (rule-based)" }}
          · {{ fmtDateTime(data.ai_summary_at) }} · verify against the record below.
        </p>
      </section>

      <section
        v-for="(g, gi) in groups"
        :key="gi"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2
          v-if="g.title"
          class="mb-4 font-semibold"
        >
          {{ g.title }}
        </h2>
        <dl class="grid gap-3">
          <template
            v-for="r in g.rows"
            :key="r.key"
          >
            <!-- Repeating-row grid (Heights & Staggers, Jumpers, …) -->
            <div
              v-if="r.def['x-widget'] === 'grid'"
              class="border-b border-neutral-50 pb-3 last:border-0"
            >
              <p class="mb-2 text-sm font-medium text-neutral-700">
                {{ r.def.title ?? r.key }}
              </p>
              <p
                v-if="gridRows(r.value).length === 0"
                class="text-sm text-neutral-400"
              >
                No rows recorded.
              </p>
              <div
                v-else
                class="overflow-x-auto"
              >
                <table class="w-full text-xs">
                  <thead class="bg-neutral-50 text-left text-neutral-500">
                    <tr>
                      <th
                        v-for="col in r.def['x-columns'] ?? []"
                        :key="col.key"
                        class="px-2 py-1 font-medium"
                      >
                        {{ col.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, ri) in gridRows(r.value)"
                      :key="ri"
                      class="border-t border-neutral-100"
                    >
                      <td
                        v-for="col in r.def['x-columns'] ?? []"
                        :key="col.key"
                        class="px-2 py-1"
                      >
                        {{ gridCell(col, row) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- Single-value rows -->
            <div
              v-else
              class="grid grid-cols-[1fr_auto] items-start gap-4 border-b border-neutral-50 pb-2 last:border-0"
            >
              <dt class="text-sm text-neutral-700">
                <span
                  v-if="r.def['x-hold-point']"
                  class="mr-1 rounded bg-semantic-warning/20 px-1 text-[10px] font-bold text-semantic-warning"
                >HOLD</span>
                <span
                  v-else-if="r.def['x-witness-point']"
                  class="mr-1 rounded bg-brand-accent/15 px-1 text-[10px] font-bold text-brand-accent"
                >WITNESS</span>
                {{ r.def.title ?? r.key }}
              </dt>
              <dd class="text-right text-sm font-medium">
                <!-- Tri-state -->
                <span v-if="r.def['x-widget'] === 'yes-no-na'">
                  {{ YESNO[r.value as string] ?? "—" }}
                </span>
                <!-- Measured: design ref + actual + compliance -->
                <span v-else-if="r.def['x-widget'] === 'measured'">
                  <span class="text-neutral-400">
                    design {{ plain(r.def['x-design']) }}{{ r.def['x-unit'] ? " " + r.def['x-unit'] : "" }}
                    →
                  </span>
                  {{ plain(asMeasured(r.value).actual) }}{{ r.def['x-unit'] ? " " + r.def['x-unit'] : "" }}
                  <span
                    v-if="asMeasured(r.value).compliant"
                    class="ml-1 text-neutral-400"
                  >({{ YESNO[asMeasured(r.value).compliant as string] }})</span>
                </span>
                <!-- Photos -->
                <span v-else-if="r.def['x-widget'] === 'photo-list'">
                  {{ Array.isArray(r.value) ? r.value.length : 0 }} photo(s)
                </span>
                <!-- Attestation -->
                <span v-else-if="r.def['x-widget'] === 'checkbox-attestation'">
                  {{ r.value ? "✓" : "—" }}
                </span>
                <!-- Everything else -->
                <span v-else>{{ plain(r.value) }}</span>
              </dd>
            </div>
          </template>
        </dl>
      </section>

      <!-- Photos (evidence — captured in the field or attached here) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 class="font-semibold">
            Photos ({{ photoFileIds.length }})
          </h2>
          <label
            v-if="data.status !== 'approved'"
            class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            :class="photoBusy ? 'pointer-events-none opacity-50' : ''"
          >
            {{ photoBusy ? "Uploading…" : "+ Add photo" }}
            <input
              type="file"
              accept="image/*"
              multiple
              class="hidden"
              @change="onPhotoPick"
            >
          </label>
        </div>
        <FilePhotoGrid
          v-if="photoFileIds.length"
          :file-ids="photoFileIds"
          :removable="data.status !== 'approved'"
          @remove="removePhoto"
        />
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No photos attached.
          <span v-if="data.status !== 'approved'">
            Use “+ Add photo”, or capture them on the mobile app in the field.
          </span>
        </p>
        <p
          v-if="photoError"
          class="mt-2 text-sm text-semantic-danger"
        >
          {{ photoError }}
        </p>
      </section>

      <!-- Marked-up drawings (visual evidence) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
          <h2 class="font-semibold">
            Drawings
          </h2>
          <div
            v-if="data.status !== 'approved'"
            class="flex flex-wrap items-center gap-2"
          >
            <button
              v-if="(registerDrawings ?? []).length"
              type="button"
              class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-brand-primary-on transition hover:opacity-90"
              @click="showRegisterPicker = !showRegisterPicker; showAttachDrawing = false; selectedRegisterDrawing = null"
            >
              {{ showRegisterPicker ? "Cancel" : "+ From Drawings" }}
            </button>
            <button
              v-if="availableDrawings.length"
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              @click="showAttachDrawing = !showAttachDrawing; showRegisterPicker = false"
            >
              {{ showAttachDrawing ? "Cancel" : "Attach work-pack drawing" }}
            </button>
          </div>
        </div>

        <!-- Attach a marked-up page from the Drawings register -->
        <div
          v-if="showRegisterPicker"
          class="mb-4 rounded-lg bg-neutral-50 p-3 text-sm"
        >
          <template v-if="!selectedRegisterDrawing">
            <p class="mb-1 text-xs text-neutral-500">
              Choose a drawing:
            </p>
            <ul class="grid gap-1">
              <li
                v-for="dr in (registerDrawings ?? [])"
                :key="dr.id"
                class="flex items-center justify-between gap-3"
              >
                <span>
                  <span class="font-medium text-neutral-900">{{ dr.name }}</span>
                  <span class="text-neutral-400"> · {{ dr.page_count }} page(s)</span>
                </span>
                <button
                  type="button"
                  class="shrink-0 text-brand-primary hover:underline"
                  @click="selectedRegisterDrawing = dr"
                >
                  Choose
                </button>
              </li>
            </ul>
          </template>
          <template v-else>
            <div class="mb-2 flex items-center gap-2">
              <button
                type="button"
                class="text-neutral-400 hover:text-neutral-900"
                @click="selectedRegisterDrawing = null"
              >
                ‹ Back
              </button>
              <span class="font-medium text-neutral-900">
                {{ selectedRegisterDrawing!.name }}
              </span>
            </div>
            <div
              v-if="registerPages(selectedRegisterDrawing!).length"
              class="flex flex-wrap gap-2"
            >
              <button
                v-for="pg in registerPages(selectedRegisterDrawing!)"
                :key="pg.pageNumber"
                type="button"
                class="rounded-md border border-neutral-300 bg-neutral-0 px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50"
                :disabled="drawBusy"
                @click="attachFromRegister(selectedRegisterDrawing!, pg)"
              >
                Attach page {{ pg.pageNumber }}
              </button>
            </div>
            <p
              v-else
              class="text-neutral-400"
            >
              This drawing has no pages yet.
            </p>
            <p
              v-if="drawBusy"
              class="mt-2 text-xs text-neutral-500"
            >
              Preparing the page snapshot…
            </p>
          </template>
        </div>

        <!-- Attach an already-marked-up work-pack drawing -->
        <div
          v-if="showAttachDrawing"
          class="mb-4 rounded-lg border border-neutral-100 p-3"
        >
          <ul class="grid gap-1 text-sm">
            <li
              v-for="{ pack, doc } in availableDrawings"
              :key="doc.document_id"
              class="flex items-center justify-between gap-3"
            >
              <span>{{ doc.title }}<span class="text-neutral-400"> · {{ pack.reference ?? pack.title }}</span></span>
              <button
                type="button"
                class="rounded-md bg-brand-primary px-3 py-1 text-xs font-semibold text-brand-primary-on"
                @click="attachDrawing(pack, doc)"
              >
                Attach
              </button>
            </li>
          </ul>
        </div>

        <p
          v-if="(data.drawings ?? []).length === 0"
          class="text-sm text-neutral-400"
        >
          No drawings attached.
          <span
            v-if="data.status !== 'approved' && (registerDrawings ?? []).length"
            class="text-neutral-400"
          >
            (Use “+ From Drawings” to attach a marked-up page from the register.)
          </span>
        </p>
        <div
          v-else
          class="grid gap-4 sm:grid-cols-2"
        >
          <div
            v-for="d in data.drawings"
            :key="d.drawing_id as string"
          >
            <div class="mb-1 flex items-center justify-between text-sm">
              <span class="font-medium">{{ d.title }}</span>
              <button
                type="button"
                class="text-neutral-300 hover:text-semantic-danger"
                title="Remove"
                @click="removeDrawing(d.drawing_id as string)"
              >
                ✕
              </button>
            </div>
            <DiaryMarkedUpDrawing :drawing="d" />
          </div>
        </div>
      </section>

      <!-- Record: attribution + inspector signature -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Record
        </h2>
        <dl class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <template v-if="attribution(data.created_by_name, data.created_at)">
            <dt class="text-neutral-500">
              Created by
            </dt>
            <dd>{{ attribution(data.created_by_name, data.created_at) }}</dd>
          </template>
          <template v-if="attribution(data.signed_off_by_name, data.signed_off_at)">
            <dt class="text-neutral-500">
              Signed off by
            </dt>
            <dd>{{ attribution(data.signed_off_by_name, data.signed_off_at) }}</dd>
          </template>
          <template v-if="data.signed_off_signature">
            <dt class="text-neutral-500">
              Inspector signature
            </dt>
            <dd>
              <a
                :href="data.signed_off_signature"
                target="_blank"
                rel="noopener"
                title="Open full size"
              >
                <img
                  :src="data.signed_off_signature"
                  alt="inspector signature"
                  class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                >
              </a>
            </dd>
          </template>
          <template v-if="signoffGeo">
            <dt class="text-neutral-500">
              Sign-off location
            </dt>
            <dd>
              <a
                :href="signoffGeo.href"
                target="_blank"
                rel="noopener"
                class="text-brand-primary hover:underline"
              >{{ signoffGeo.label }}</a>
            </dd>
          </template>
          <template v-if="attribution(data.approved_by_name, data.approved_at)">
            <dt class="text-neutral-500">
              Approved by
            </dt>
            <dd>{{ attribution(data.approved_by_name, data.approved_at) }}</dd>
          </template>
          <template v-if="data.approved_signature">
            <dt class="text-neutral-500">
              Approver signature
            </dt>
            <dd>
              <a
                :href="data.approved_signature"
                target="_blank"
                rel="noopener"
                title="Open full size"
              >
                <img
                  :src="data.approved_signature"
                  alt="approver signature"
                  class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                >
              </a>
            </dd>
          </template>
          <template v-if="attribution(data.client_accepted_by_name, data.client_accepted_at)">
            <dt class="text-neutral-500">
              Accepted by client
            </dt>
            <dd>{{ attribution(data.client_accepted_by_name, data.client_accepted_at) }}</dd>
          </template>
          <template v-if="attribution(data.last_updated_by_name, data.last_updated_at)">
            <dt class="text-neutral-500">
              Last updated by
            </dt>
            <dd>{{ attribution(data.last_updated_by_name, data.last_updated_at) }}</dd>
          </template>
        </dl>

        <h3 class="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          History
        </h3>
        <ol class="grid gap-1 text-sm">
          <li
            v-for="h in history ?? []"
            :key="h.seq"
            class="flex flex-wrap gap-x-2 text-neutral-600"
          >
            <span class="font-medium text-neutral-800">{{ h.label }}</span>
            <span class="text-neutral-400">·</span>
            <span>{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">·</span>
            <span class="text-neutral-400">{{ fmtDateTime(h.at) }}</span>
          </li>
        </ol>
      </section>
    </div>
  </PageLayout>
</template>
